import React, { useCallback } from 'react';
import { EmailApiResponse } from '@dreamai/types';
import RecipientList from '../RecipientList';
import Downshift from 'downshift';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchContactSuggestions } from './emailApiHelpers';

import { ReactComponent as CloseIcon } from '../../../../../icons/pill-delete.svg';
import './email-metadata.scss';

// Option type for react-select
interface ContactOption {
  value: string; // email address
  label: string; // display name or email
  email: string; // email address (same as value)
  name?: string; // display name if available
  imageUrl?: string; // profile image URL if available
  isCustomInput?: boolean; // flag to identify user-typed options
}

// Props for the reusable recipient field component
interface RecipientFieldProps {
  label: string;
  value?: string;
  onChange?: (value: string) => void;
  className: string;
  loadOptions: (inputValue: string) => Promise<ContactOption[]>;
}

interface EmailMetadataProps {
  email: EmailApiResponse;
  localEmailId: string;
  isEditMode?: boolean;
  editTo?: string;
  editCc?: string;
  editBcc?: string;
  setEditTo?: (value: string) => void;
  setEditCc?: (value: string) => void;
  setEditBcc?: (value: string) => void;
}

// Reusable recipient field component using Downshift
const RecipientField: React.FC<RecipientFieldProps> = ({
  label,
  value,
  onChange,
  className,
  loadOptions,
}) => {
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState<ContactOption[]>([]);
  const [selectedItems, setSelectedItems] = React.useState<ContactOption[]>([]);
  const debounceTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Convert comma-separated string to options array
  const stringToOptions = (inputString: string = ''): ContactOption[] => {
    if (!inputString.trim()) return [];
    return inputString.split(',').map((email) => {
      const trimmedEmail = email.trim();
      return {
        value: trimmedEmail,
        label: trimmedEmail,
        email: trimmedEmail,
      };
    });
  };

  // Convert options array to comma-separated string
  const optionsToString = (options: ContactOption[]): string => {
    return options.map((option) => option.value).join(', ');
  };

  // Initialize selected items from value prop
  React.useEffect(() => {
    setSelectedItems(stringToOptions(value));
  }, [value]);

  // Load options with debouncing
  const loadOptionsDebounced = React.useCallback(
    (input: string) => {
      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Always include custom input option if there's input
      const customInputOption: ContactOption[] = input.trim()
        ? [
            {
              value: input.trim(),
              label: input.trim(),
              email: input.trim(),
              isCustomInput: true,
            },
          ]
        : [];

      // Set immediate options with custom input
      setOptions(customInputOption);

      // Load contact suggestions with debounce
      if (input.length >= 2) {
        debounceTimeoutRef.current = setTimeout(async () => {
          try {
            const contactOptions = await loadOptions(input);
            setOptions([...customInputOption, ...contactOptions]);
          } catch (error) {
            console.error('Error loading contact options:', error);
            // Keep custom input option on error
            setOptions(customInputOption);
          }
        }, 300);
      }
    },
    [loadOptions],
  );

  // Handle input change
  const handleInputChange = (input: string) => {
    setInputValue(input);
    loadOptionsDebounced(input);
  };

  // Handle item selection
  const handleSelection = (selectedItem: ContactOption | null) => {
    if (!selectedItem) {
      console.log('No selectedItem, returning');
      return;
    }

    // For custom input, the value is already stored in the option
    // Don't use current inputValue as it may be cleared by race condition
    const itemToAdd = selectedItem;

    // Avoid duplicates
    const isDuplicate = selectedItems.some(
      (item) => item.value === itemToAdd.value,
    );

    if (!isDuplicate) {
      const newSelectedItems = [...selectedItems, itemToAdd];
      setSelectedItems(newSelectedItems);
      if (onChange) {
        onChange(optionsToString(newSelectedItems));
      }
    }

    // Clear input and options after selection
    setInputValue('');
    setOptions([]);
  };

  // Remove selected item
  const removeItem = (itemToRemove: ContactOption) => {
    const newSelectedItems = selectedItems.filter(
      (item) => item.value !== itemToRemove.value,
    );
    setSelectedItems(newSelectedItems);
    if (onChange) {
      onChange(optionsToString(newSelectedItems));
    }
  };

  // Create key handler that will be called with highlightedIndex
  const createKeyDownHandler = (highlightedIndex: number | null) => {
    return (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && inputValue.trim()) {
        // Only select input if no dropdown option is highlighted
        if (highlightedIndex === null || highlightedIndex === -1) {
          event.preventDefault();
          const customInputItem: ContactOption = {
            value: inputValue.trim(),
            label: inputValue.trim(),
            email: inputValue.trim(),
            isCustomInput: true,
          };
          handleSelection(customInputItem);
        }
        // Note: If a dropdown option is highlighted, Downshift will handle the selection
      } else if (
        event.key === 'Backspace' &&
        inputValue === '' &&
        selectedItems.length > 0
      ) {
        // Remove last selected item when backspacing on empty input
        event.preventDefault();
        const newSelectedItems = selectedItems.slice(0, -1);
        setSelectedItems(newSelectedItems);
        if (onChange) {
          onChange(optionsToString(newSelectedItems));
        }
      }
    };
  };

  return (
    <div className='email-multi-field'>
      <label className='email-field-label'>{label}:</label>
      <div className={`email-field-input ${className}`}>
        {/* Downshift typeahead with inline selected items */}
        <Downshift
          onChange={handleSelection}
          inputValue={inputValue}
          onInputValueChange={handleInputChange}
          itemToString={() => ''}
        >
          {({
            getInputProps,
            getItemProps,
            getMenuProps,
            isOpen,
            highlightedIndex,
            getRootProps,
          }) => (
            <div
              {...getRootProps({}, { suppressRefError: true })}
              className='downshift-container'
            >
              {/* Container for selected items and input */}
              <div className='input-container'>
                {/* Selected items inline */}
                {selectedItems.map((item, index) => (
                  <div key={index} className='selected-item'>
                    <span>{item.label}</span>
                    <button
                      type='button'
                      onClick={() => removeItem(item)}
                      className='remove-item'
                    >
                      <CloseIcon />
                    </button>
                  </div>
                ))}

                {/* Input field */}
                <input
                  {...getInputProps({
                    className: 'typeahead-input',
                    onKeyDown: createKeyDownHandler(highlightedIndex),
                  })}
                />
              </div>

              {/* Dropdown menu */}
              {isOpen && options.length > 0 && (
                <div {...getMenuProps()} className='options-menu'>
                  {/* Custom input options */}
                  {options
                    .filter((option) => option.isCustomInput)
                    .map((option, index) => (
                      <div
                        key={`custom-${index}`}
                        {...getItemProps({
                          item: option,
                          index,
                          className: `option ${highlightedIndex === index ? 'highlighted' : ''}`,
                        })}
                      >
                        {option.label}
                      </div>
                    ))}

                  {/* Contact suggestions with header */}
                  {options.filter((option) => !option.isCustomInput).length >
                    0 && (
                    <>
                      <div className='contacts-header'>From your contacts</div>
                      {options
                        .filter((option) => !option.isCustomInput)
                        .map((option, index) => {
                          const adjustedIndex =
                            options.filter((opt) => opt.isCustomInput).length +
                            index;
                          return (
                            <div
                              key={`contact-${index}`}
                              {...getItemProps({
                                item: option,
                                index: adjustedIndex,
                                className: `option ${highlightedIndex === adjustedIndex ? 'highlighted' : ''}`,
                              })}
                            >
                              {option.imageUrl && (
                                <img
                                  src={option.imageUrl}
                                  alt=''
                                  className='option-avatar'
                                />
                              )}
                              <span>{option.label}</span>
                            </div>
                          );
                        })}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </Downshift>
      </div>
    </div>
  );
};

const EmailMetadata: React.FC<EmailMetadataProps> = ({
  email,
  localEmailId,
  isEditMode,
  editTo,
  editCc,
  editBcc,
  setEditTo,
  setEditCc,
  setEditBcc,
}) => {
  // Only show edit fields in edit mode for drafts
  const showEditFields = isEditMode && email.isDraft;
  const { getAccessTokenSilently } = useAuth0();

  // Load contact suggestions when user types
  const loadContactSuggestions = useCallback(
    async (inputValue: string) => {
      if (!inputValue || inputValue.length < 2) {
        return [];
      }

      try {
        const contacts = await fetchContactSuggestions(
          localEmailId,
          inputValue,
          getAccessTokenSilently,
        );

        return contacts.map((contact) => ({
          value: contact.email,
          label: contact.name
            ? `${contact.name} <${contact.email}>`
            : contact.email,
          email: contact.email,
          name: contact.name,
          imageUrl: contact.imageUrl,
        }));
      } catch (error) {
        console.error('Error loading contact suggestions:', error);
        return [];
      }
    },
    [localEmailId, getAccessTokenSilently],
  );

  return (
    <div className='email-metadata'>
      {showEditFields ? (
        // Editable header fields for drafts in edit mode
        <div className='header-edit-mode'>
          <RecipientField
            label='To'
            value={editTo}
            onChange={setEditTo}
            className='email-to-input'
            loadOptions={loadContactSuggestions}
          />
          <RecipientField
            label='CC'
            value={editCc}
            onChange={setEditCc}
            className='email-cc-input'
            loadOptions={loadContactSuggestions}
          />
          <RecipientField
            label='BCC'
            value={editBcc}
            onChange={setEditBcc}
            className='email-bcc-input'
            loadOptions={loadContactSuggestions}
          />
        </div>
      ) : (
        // Regular display mode
        <>
          {/* Show From for sent/draft emails, To for inbox emails */}
          {email.isSent || email.isDraft ? (
            <RecipientList label='To' recipients={email.to} />
          ) : (
            <div className='metadata-item'>
              <strong>From:</strong> {email.from}
            </div>
          )}

          {/* Always show CC and BCC if they exist */}
          {email.cc && email.cc.length > 0 && (
            <RecipientList label='CC' recipients={email.cc} />
          )}
          {email.bcc && email.bcc.length > 0 && (
            <RecipientList label='BCC' recipients={email.bcc} />
          )}
        </>
      )}
    </div>
  );
};

export default EmailMetadata;
